/**
 * faceClipRecorder — turns tracked frames into a face clip (ENG-1107). Pure.
 *
 * Output is the face-clip JSON `FaceClipPlayer` already plays —
 * `{ fps, frames: [{ timeCode, blendshapeWeights[52] }] }`, timeCode in SECONDS
 * from the first frame (the same contract convert_face_fbx_to_arkit.py emits)
 * — plus a `capture` block the player ignores: the raw MediaPipe landmarks,
 * the head matrix, and (Track B) the fitted GNM parameters, per frame, as
 * compact base64 (faceCaptureCodec.js). The landmarks are the EVIDENCE — the
 * fit is derived and will improve; the capture cannot be redone.
 *
 * Runs at capture cadence (the tracker's 15-30 Hz), never on the render loop,
 * so a small per-frame allocation here is fine. A hard `maxMs` cap stops the
 * take with `stoppedReason = 'max-duration'` rather than growing without
 * bound (an inline-JSON recording is ~5.5 MB/min).
 */
import { packInt16Base64, packFloat32Base64 } from './faceCaptureCodec.js'

export const LANDMARK_COUNT = 478
export const GNM_EXPRESSION_DIM = 383
export const GNM_ROTATION_DIM = 12
export const CAPTURE_VERSION = 1
export const CAPTURE_SOURCE = 'mediapipe-face-landmarker'
export const CAPTURE_SOURCE_VERSION = '0.10.17'
export const LANDMARK_SCALE = 1e-4
export const GNM_EXPRESSION_SCALE = 1e-3

const round4 = (v) => Math.round(v * 1e4) / 1e4

export function createFaceClipRecorder({ fps = 24, maxMs = 120_000 } = {}) {
  let active = false
  let t0 = NaN
  let armedAt = 0 // eslint-disable-line no-unused-vars -- kept for a future 'time since REC' readout
  let lastT = -Infinity
  let stoppedReason = null
  let frames = []
  let landmarks = [] // Float32Array(1434) | null per frame
  let matrices = [] // Float32Array(16) | null per frame
  let gnms = [] // { expression, rotations } | null per frame
  let gnmMeta = null

  function reset() {
    frames = []; landmarks = []; matrices = []; gnms = []; gnmMeta = null
    lastT = -Infinity
  }

  function buildCapture() {
    const n = frames.length
    const L = new Float32Array(n * LANDMARK_COUNT * 3)
    const M = new Float32Array(n * 16)
    for (let i = 0; i < n; i++) {
      if (landmarks[i]) L.set(landmarks[i].subarray(0, LANDMARK_COUNT * 3), i * LANDMARK_COUNT * 3)
      if (matrices[i]) M.set(matrices[i].subarray(0, 16), i * 16)
    }
    let gnm = null
    if (gnmMeta) {
      const E = new Float32Array(n * GNM_EXPRESSION_DIM)
      const R = new Float32Array(n * GNM_ROTATION_DIM)
      for (let i = 0; i < n; i++) {
        if (gnms[i]) {
          E.set(gnms[i].expression.subarray(0, GNM_EXPRESSION_DIM), i * GNM_EXPRESSION_DIM)
          R.set(gnms[i].rotations.subarray(0, GNM_ROTATION_DIM), i * GNM_ROTATION_DIM)
        }
      }
      gnm = {
        model: gnmMeta.model,
        basis: gnmMeta.basis,
        expression: { encoding: 'int16-base64', scale: GNM_EXPRESSION_SCALE, data: packInt16Base64(E, GNM_EXPRESSION_SCALE) },
        rotations: { encoding: 'float32-base64', data: packFloat32Base64(R) },
      }
    }
    return {
      version: CAPTURE_VERSION,
      source: CAPTURE_SOURCE,
      sourceVersion: CAPTURE_SOURCE_VERSION,
      fps,
      frameCount: n,
      landmarkCount: LANDMARK_COUNT,
      t: frames.map((f) => f.timeCode),
      landmarks: { encoding: 'int16-base64', scale: LANDMARK_SCALE, data: packInt16Base64(L, LANDMARK_SCALE) },
      matrix: { encoding: 'float32-base64', data: packFloat32Base64(M) },
      gnm,
    }
  }

  return {
    get active() { return active },
    get frameCount() { return frames.length },
    get elapsedMs() { return frames.length ? Math.round(frames[frames.length - 1].timeCode * 1000) : 0 },
    get stoppedReason() { return stoppedReason },

    /** Arm the take. The clip's time origin is the FIRST frame pushed, not this
     *  call: the REC click lands between two tracker ticks, and taking it as t=0
     *  put a silent gap (up to a whole lost-face interval) at the head of every
     *  take. `tMs` is accepted for the cap only. */
    begin(tMs) {
      reset()
      active = true
      t0 = NaN
      armedAt = tMs
      stoppedReason = null
    },

    /** @returns {boolean} whether the frame was recorded */
    push(arkit, tMs, extra = null) {
      if (!active) return false
      if (tMs <= lastT) return false
      if (Number.isNaN(t0)) t0 = tMs
      if (tMs - t0 > maxMs) {
        active = false
        stoppedReason = 'max-duration'
        return false
      }
      lastT = tMs
      const weights = new Array(52)
      for (let i = 0; i < 52; i++) weights[i] = round4(arkit[i] || 0)
      frames.push({ timeCode: round4((tMs - t0) / 1000), blendshapeWeights: weights })
      landmarks.push(extra?.landmarks ? Float32Array.from(extra.landmarks) : null)
      matrices.push(extra?.matrix ? Float32Array.from(extra.matrix) : null)
      const g = extra?.gnm
      if (g) {
        gnms.push({ expression: Float32Array.from(g.expression), rotations: Float32Array.from(g.rotations) })
        if (!gnmMeta) gnmMeta = { model: g.model ?? 'gnm-head-v3_0', basis: g.basis ?? 'unknown' }
      } else {
        gnms.push(null)
      }
      return true
    },

    /** @returns {object|null} the clip, or null for an empty take */
    finish() {
      active = false
      if (frames.length === 0) { reset(); return null }
      const clip = { fps, frames, capture: buildCapture() }
      reset()
      return clip
    },

    discard() {
      active = false
      stoppedReason = null
      reset()
    },
  }
}